// ULTRA ARCADE - API CLIENT
// Centralized API client for Laravel backend communication

const API_BASE_URL = (process.env.NEXT_PUBLIC_API_URL || '/api').replace(/\/+$/, '');
const USER_TOKEN_KEY = 'auth_token';
const USER_REFRESH_TOKEN_KEY = 'auth_refresh_token';
const AUTH_USER_KEY = 'auth_user';
const ADMIN_TOKEN_KEY = 'admin_auth_token';

function unwrapApiEnvelope(payload) {
  if (payload && typeof payload === 'object' && payload.success === true && payload.data !== undefined) {
    return payload.data;
  }
  return payload;
}

class ApiClient {
  constructor() {
    this.baseUrl = API_BASE_URL;
    this.token = null;
    this.adminToken = null;
  }

  // Get stored auth token
  getToken() {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(USER_TOKEN_KEY);
    }
    return this.token;
  }

  // Get stored admin auth token
  getAdminToken() {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(ADMIN_TOKEN_KEY) || localStorage.getItem(USER_TOKEN_KEY);
    }
    return this.adminToken || this.token;
  }

  // Set auth token
  setToken(token) {
    this.token = token;
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem(USER_TOKEN_KEY, token);
      } else {
        localStorage.removeItem(USER_TOKEN_KEY);
      }
    }
  }

  // Set admin auth token used for /admin routes
  setAdminToken(token) {
    this.adminToken = token;
    if (typeof window !== 'undefined') {
      if (token) {
        localStorage.setItem(ADMIN_TOKEN_KEY, token);
        // Keep backward compatibility for modules still using auth_token.
        localStorage.setItem(USER_TOKEN_KEY, token);
      } else {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
      }
    }
  }

  clearAdminToken() {
    this.adminToken = null;
    if (typeof window !== 'undefined') {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
      // Keep existing logout behavior for current admin area usage.
      localStorage.removeItem(USER_TOKEN_KEY);
    }
  }

  // Build headers
  getHeaders(customHeaders = {}, endpoint = '') {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...customHeaders,
    };

    const isAdminEndpoint = /^\/admin(?:\/|$)/.test(String(endpoint || ''));
    const token = isAdminEndpoint ? this.getAdminToken() : this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // Core request method
  async request(endpoint, options = {}) {
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
    
    const config = {
      headers: this.getHeaders(options.headers, endpoint),
      credentials: 'include',
      ...options,
    };

    if (options.body && typeof options.body === 'object') {
      config.body = JSON.stringify(options.body);
    }

    try {
      const response = await fetch(url, config);
      
      // Handle 401 unauthorized
      if (response.status === 401) {
        if (/^\/admin(?:\/|$)/.test(String(endpoint || ''))) {
          this.clearAdminToken();
        } else {
          this.setToken(null);
        }
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth:logout'));
        }
      }

      const data = await response.json();

      if (!response.ok) {
        throw new ApiError(data.message || 'Request failed', response.status, data);
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError(error.message || 'Network error', 500);
    }
  }

  // HTTP Methods
  get(endpoint, params = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  post(endpoint, body = {}) {
    return this.request(endpoint, { method: 'POST', body });
  }

  put(endpoint, body = {}) {
    return this.request(endpoint, { method: 'PUT', body });
  }

  patch(endpoint, body = {}) {
    return this.request(endpoint, { method: 'PATCH', body });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // File upload
  async upload(endpoint, formData) {
    const url = `${this.baseUrl}${endpoint}`;
    const headers = {};
    
    const isAdminEndpoint = /^\/admin(?:\/|$)/.test(String(endpoint || ''));
    const token = isAdminEndpoint ? this.getAdminToken() : this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include',
    });

    const data = await response.json();
    if (!response.ok) {
      throw new ApiError(data.message || 'Upload failed', response.status, data);
    }

    return data;
  }
}

// Custom API Error class
class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Create singleton instance
const api = new ApiClient();

export function setAdminAuthToken(token) {
  api.setAdminToken(token);
}

export function clearAdminAuthToken() {
  api.clearAdminToken();
}

export async function adminFetch(input, init = {}) {
  const requestUrl = typeof input === 'string' ? input : input?.url || '';
  const isAdminApiRoute = /^\/api\/admin(?:\/|$)/.test(requestUrl);
  const isAdminSessionRoute = /^\/api\/admin\/session(?:\?|$)/.test(requestUrl);

  if (!isAdminApiRoute || isAdminSessionRoute) {
    return fetch(input, {
      credentials: init.credentials || 'include',
      ...init,
    });
  }

  const headers = new Headers(init.headers || {});
  const adminToken = api.getAdminToken();

  if (adminToken && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${adminToken}`);
  }

  return fetch(input, {
    credentials: init.credentials || 'include',
    ...init,
    headers,
  });
}

// ============================================
// GAME API METHODS
// ============================================

export async function getGames(params = {}) {
  return api.get('/games', params);
}

export async function getGame(slug) {
  return api.get(`/games/${slug}`);
}

export async function getHotGames(params = {}) {
  return api.get('/games', { popularity: 'hot', ...params });
}

export async function getNewGames(params = {}) {
  return api.get('/games', { popularity: 'new', ...params });
}

export async function getTrendingGames(params = {}) {
  return api.get('/games', { featured: true, sort: 'popular', ...params });
}

export async function getRandomGame() {
  return api.get('/games/random');
}

export async function getRelatedGames(slug, limit = 8) {
  return api.get(`/games/${slug}/related`, { limit });
}

export async function incrementPlayCount(slug) {
  return api.post(`/games/${slug}/play`);
}

// ============================================
// CATEGORY API METHODS
// ============================================

// ── Backend API Functions (for SSG/Server Context) ──────────────────────────
function getBackendBaseUrl() {
  const rawBaseUrl =
    process.env.BACKEND_URL ||
    process.env.NEXT_PUBLIC_BACKEND_URL ||
    process.env.BACKEND_API_URL ||
    process.env.NEXT_PUBLIC_API_URL ||
    'http://localhost:3001';

  const normalized = String(rawBaseUrl).replace(/\/+$/, '');
  return normalized.endsWith('/api') ? normalized.slice(0, -4) : normalized;
}

function sanitizeGameImageUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const lowerRaw = raw.toLowerCase();
  if (lowerRaw === 'http' || lowerRaw === 'https' || lowerRaw === 'http://' || lowerRaw === 'https://') {
    return '';
  }

  if (raw.startsWith('/')) {
    return raw;
  }

  try {
    const parsed = new URL(raw);
    const hostname = String(parsed.hostname || '').toLowerCase();
    if (hostname === 'images.crazygames.com') {
      return '';
    }
    return parsed.toString();
  } catch {
    return '';
  }
}

function normalizeGameMedia(game) {
  const thumbnail = sanitizeGameImageUrl(game?.thumbnail);
  const thumb = sanitizeGameImageUrl(game?.thumb);
  const image = sanitizeGameImageUrl(game?.image);
  const imageUrl = sanitizeGameImageUrl(game?.image_url);
  const poster = sanitizeGameImageUrl(game?.poster);
  const safePrimary = thumbnail || thumb || image || imageUrl || poster || '';

  return {
    ...game,
    thumbnail: thumbnail || safePrimary,
    thumb: thumb || safePrimary,
    image: image || safePrimary,
    image_url: imageUrl || safePrimary,
    poster: poster || safePrimary,
  };
}

export async function getGamesFromBackend(params = {}) {
  const requestedLimit = Number(params.limit || 0);
  if (Number.isFinite(requestedLimit) && requestedLimit > 100) {
    const games = [];
    const startOffset = Math.max(0, Number(params.offset) || 0);
    while (games.length < requestedLimit) {
      const batchLimit = Math.min(100, requestedLimit - games.length);
      const batch = await getGamesFromBackend({ ...params, limit: batchLimit, offset: startOffset + games.length });
      games.push(...batch);
      if (batch.length < batchLimit) break;
    }
    return games;
  }
  try {
    const backendUrl = getBackendBaseUrl();
    const query = new URLSearchParams();

    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      query.set(key, String(value));
    });

    const queryString = query.toString();
    const response = await fetch(
      `${backendUrl}/api/games${queryString ? `?${queryString}` : ''}`,
      {
        headers: { 'Content-Type': 'application/json' },
        next: { revalidate: 60 },
      }
    );

    if (!response.ok) throw new Error(`Backend returned ${response.status}`);

    const payload = await response.json();
    const items = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.games)
        ? payload.games
        : [];

    return items.map(normalizeGameMedia);
  } catch (error) {
    console.error('Failed to fetch games from backend:', error);
    return [];
  }
}

export async function getCategoriesFromBackend(params = {}) {
  try {
    const backendUrl = getBackendBaseUrl();
    const query = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      query.set(key, String(value));
    });

    const queryString = query.toString();
    const response = await fetch(`${backendUrl}/api/categories${queryString ? `?${queryString}` : ''}`, {
      headers: { 'Content-Type': 'application/json' },
      next: { revalidate: 60 },
    });
    
    if (!response.ok) throw new Error(`Backend returned ${response.status}`);
    
    const payload = await response.json();
    const categories = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.categories)
        ? payload.categories
        : [];
    
    return categories.map(cat => ({
      id: cat.id,
      slug: cat.slug,
      name: cat.name,
      icon: cat.icon || null,
      color: cat.color || '#64748b',
      games: cat.games || 0,
      description: cat.description || null,
      showHome: cat.showHome ?? cat.show_home ?? true,
      showcaseOrder: Number.isFinite(Number(cat.showcaseOrder ?? cat.showcase_order))
        ? Number(cat.showcaseOrder ?? cat.showcase_order)
        : null,
    }));
  } catch (error) {
    console.error('Failed to fetch categories from backend:', error);
    return [];
  }
}

export async function getCategoryGamesFromBackend({ categoryId = '', categorySlug = '', limit = 12, offset = 0 } = {}) {
  try {
    const backendUrl = getBackendBaseUrl();
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });

    if (categoryId) params.set('categoryId', String(categoryId));
    if (categorySlug) params.set('category', String(categorySlug));

    const response = await fetch(`${backendUrl}/api/games?${params.toString()}`, {
      headers: { 'Content-Type': 'application/json' },
      next: { revalidate: 60 },
    });

    if (!response.ok) throw new Error(`Backend returned ${response.status}`);

    const payload = await response.json();
    const items = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.games)
        ? payload.games
        : [];

    const hasMore = typeof payload?.hasMore === 'boolean'
      ? payload.hasMore
      : Boolean(payload?.meta?.hasMore);

    return {
      games: items.map(normalizeGameMedia),
      hasMore,
    };
  } catch (error) {
    console.error('Failed to fetch category games from backend:', error);
    return { games: [], hasMore: false };
  }
}

export async function getCategories() {
  return api.get('/categories');
}

export async function getCategory(slug) {
  return api.get(`/categories/${slug}`);
}

export async function getCategoryGames(slug, params = {}) {
  return api.get(`/categories/${slug}/games`, params);
}

// ============================================
// SEARCH API METHODS
// ============================================

export async function searchGames(query, params = {}) {
  return api.get('/search', { q: query, ...params });
}

export async function getSearchSuggestions(query) {
  return api.get('/search/suggestions', { q: query });
}

// ============================================
// VOTE API METHODS
// ============================================

export async function voteGame(gameId, type) {
  return api.post('/vote', { game_id: gameId, type });
}

export async function removeVote(gameId) {
  return api.delete(`/vote/${gameId}`);
}

// ============================================
// COMMENT API METHODS
// ============================================

export async function getComments(gameId, params = {}) {
  return api.get(`/games/${gameId}/comments`, params);
}

export async function postComment(gameId, content, parentId = null) {
  return api.post('/comments', { game_id: gameId, content, parent_id: parentId });
}

export async function deleteComment(commentId) {
  return api.delete(`/comments/${commentId}`);
}

export async function reportComment(commentId, reason) {
  return api.post(`/comments/${commentId}/report`, { reason });
}

// ============================================
// AUTH API METHODS
// ============================================

export async function login(email, password) {
  const response = await api.post('/auth/login', { email, password });
  const data = unwrapApiEnvelope(response) || {};

  const accessToken = data.accessToken || data.token || '';
  const refreshToken = data.refreshToken || '';

  if (accessToken) {
    api.setToken(accessToken);
  }

  if (typeof window !== 'undefined') {
    if (refreshToken) localStorage.setItem(USER_REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(
      AUTH_USER_KEY,
      JSON.stringify({
        id: data.id,
        email: data.email,
        username: data.username,
      })
    );
    window.dispatchEvent(new CustomEvent('auth:login'));
  }

  return data;
}

export async function register(data) {
  const response = await api.post('/auth/register', data);
  const payload = unwrapApiEnvelope(response) || {};

  const accessToken = payload.accessToken || payload.token || '';
  const refreshToken = payload.refreshToken || '';

  if (accessToken) {
    api.setToken(accessToken);
  }

  if (typeof window !== 'undefined') {
    if (refreshToken) localStorage.setItem(USER_REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(
      AUTH_USER_KEY,
      JSON.stringify({
        id: payload.id,
        email: payload.email,
        username: payload.username,
      })
    );
    window.dispatchEvent(new CustomEvent('auth:login'));
  }

  return payload;
}

export async function logout() {
  const refreshToken = typeof window !== 'undefined'
    ? localStorage.getItem(USER_REFRESH_TOKEN_KEY)
    : null;

  try {
    await api.post('/auth/logout', refreshToken ? { refreshToken } : {});
  } finally {
    api.setToken(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem(USER_REFRESH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
  }
}

export async function getMe() {
  const response = await api.get('/users/me');
  return unwrapApiEnvelope(response);
}

export async function refreshToken() {
  const token = typeof window !== 'undefined'
    ? localStorage.getItem(USER_REFRESH_TOKEN_KEY)
    : null;

  const response = await api.post('/auth/refresh', token ? { refreshToken: token } : {});
  const data = unwrapApiEnvelope(response) || {};
  if (data.accessToken || data.token) {
    api.setToken(data.accessToken || data.token);
  }
  return data;
}

export async function forgotPassword(email) {
  const response = await api.post('/auth/forgot-password', { email });
  return unwrapApiEnvelope(response);
}

// ============================================
// USER API METHODS
// ============================================

export async function getUserProfile() {
  const response = await api.get('/users/me');
  return unwrapApiEnvelope(response);
}

export async function updateUserProfile(data) {
  const response = await api.put('/users/me', data);
  return unwrapApiEnvelope(response);
}

export async function getUserFavorites(params = {}) {
  const response = await api.get('/users/me', params);
  return unwrapApiEnvelope(response);
}

export async function addToFavorites(gameId) {
  const response = await api.post(`/users/favorites/${gameId}`);
  return unwrapApiEnvelope(response);
}

export async function removeFromFavorites(gameId) {
  const response = await api.delete(`/users/favorites/${gameId}`);
  return unwrapApiEnvelope(response);
}

export async function getUserHistory(params = {}) {
  return api.get('/user/history', params);
}

export async function getUserAchievements() {
  return api.get('/user/achievements');
}

// ============================================
// BLOG API METHODS
// ============================================

export async function getPosts(params = {}) {
  return api.get('/posts', params);
}

export async function getPost(slug) {
  return api.get(`/posts/${slug}`);
}

// ============================================
// PAGE API METHODS
// ============================================

export async function getPages() {
  return api.get('/pages');
}

export async function getPage(slug) {
  return api.get(`/pages/${slug}`);
}

// ============================================
// ADMIN API METHODS
// ============================================

export const adminApi = {
  // Dashboard
  getDashboard: () => api.get('/admin/dashboard'),
  getStats: () => api.get('/admin/stats'),
  getMetrics: () => api.get('/admin/metrics'),
  getActivityLog: (params = {}) => api.get('/admin/activity', params),

  // Games
  getGames: (params = {}) => api.get('/admin/games', params),
  getGame: (id) => api.get(`/admin/games/${id}`),
  createGame: (data) => api.post('/admin/games', data),
  bulkCreateGames: (games) => api.post('/admin/games', { games }),
  importGames: (games, defaults = {}) => api.post('/admin/games', { games, defaults }),
  updateGame: (id, data) => api.patch(`/admin/games/${id}`, data),
  deleteGame: (id) => api.delete(`/admin/games/${id}`),
  bulkDeleteGames: (ids) => api.post('/admin/games/bulk-delete', { ids }),
  bulkUpdateGames: (ids, data) => api.post('/admin/games/bulk-update', { ids, ...data }),

  // Import
  getImportSources: () => api.get('/admin/import/sources'),
  startImport: (source, options = {}) => api.post('/admin/import', { source, ...options }),
  getImportStatus: () => api.get('/admin/import/status'),
  cancelImport: () => api.post('/admin/import/cancel'),
  getImportLogs: (params = {}) => api.get('/admin/import/logs', params),

  // Categories
  getCategories: (params = {}) => api.get('/admin/categories', params),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  reorderCategories: (order) => api.post('/admin/categories/reorder', { order }),

  // Comments
  getComments: (params = {}) => api.get('/admin/comments', params),
  approveComment: (id) => api.post(`/admin/comments/${id}/approve`),
  rejectComment: (id) => api.post(`/admin/comments/${id}/reject`),
  deleteComment: (id) => api.delete(`/admin/comments/${id}`),

  // Users
  getUsers: (params = {}) => api.get('/admin/users', params),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  banUser: (id, reason) => api.post(`/admin/users/${id}/ban`, { reason }),
  unbanUser: (id) => api.post(`/admin/users/${id}/unban`),

  // Blog
  getPosts: (params = {}) => api.get('/admin/posts', params),
  createPost: (data) => api.post('/admin/posts', data),
  updatePost: (id, data) => api.put(`/admin/posts/${id}`, data),
  deletePost: (id) => api.delete(`/admin/posts/${id}`),

  // Pages
  getPages: (params = {}) => api.get('/admin/pages', params),
  createPage: (data) => api.post('/admin/pages', data),
  updatePage: (id, data) => api.put(`/admin/pages/${id}`, data),
  deletePage: (id) => api.delete(`/admin/pages/${id}`),

  // Ads
  getAdZones: () => api.get('/admin/ads'),
  updateAdZone: (id, data) => api.put(`/admin/ads/${id}`, data),

  // SEO
  getSeoSettings: () => api.get('/admin/seo'),
  updateSeoSettings: (data) => api.put('/admin/seo', data),
  generateSitemap: () => api.post('/admin/seo/sitemap'),

  // Plugins
  getPlugins: () => api.get('/admin/plugins'),
  installPlugin: (data) => api.post('/admin/plugins', data),
  updatePlugin: (id, data) => api.put(`/admin/plugins/${id}`, data),
  uninstallPlugin: (id) => api.delete(`/admin/plugins/${id}`),
  togglePlugin: (id, enabled) => api.post(`/admin/plugins/${id}/toggle`, { enabled }),

  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),

  // Upload
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.upload('/admin/upload/image', formData);
  },
};

export { api, ApiError };
export default api;
